import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';

import { UserCommunication } from '@this+that/models';

import { useShortcutsContext } from './ShortcutsContext';
import { ShortcutContext, ShortcutKey } from './types';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';
import { isInputElementFocused } from './utils';
import { APP_PAGES } from '../../routes/constants';

export interface UseEmailListNavigationOptions {
  shouldBlockOpen?: () => boolean;
}

export interface UseEmailListNavigationProps {
  filteredCommunications: UserCommunication[];
  context: ShortcutContext;
  options?: UseEmailListNavigationOptions;
}

function scrollToEmailIndex(index: number) {
  const element = document.querySelector(`[data-email-index="${index}"]`);
  if (element) {
    element.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }
}

function buildEmailPreviewUrl(messageId: string, tab: string | null): string {
  const path = `${APP_PAGES.INBOX}/preview/${encodeURIComponent(messageId)}`;
  return tab ? `${path}?tab=${tab}` : path;
}

export function useEmailListNavigation({
  filteredCommunications,
  context,
  options,
}: UseEmailListNavigationProps) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { registerShortcut, unregisterShortcut } = useKeyboardShortcuts();
  const { selectedEmailIndex, setSelectedEmailIndex } = useShortcutsContext();

  useEffect(() => {
    const handleNavigateNext = () => {
      const maxIndex = filteredCommunications.length - 1;
      const nextIndex = Math.min(selectedEmailIndex + 1, maxIndex);
      setSelectedEmailIndex(nextIndex);
      scrollToEmailIndex(nextIndex);
    };

    const handleNavigatePrev = () => {
      const prevIndex = Math.max(selectedEmailIndex - 1, 0);
      setSelectedEmailIndex(prevIndex);
      scrollToEmailIndex(prevIndex);
    };

    registerShortcut({
      key: ShortcutKey.NEXT,
      contexts: [context],
      handler: handleNavigateNext,
      description: `Move to next item in ${context}`,
    });

    registerShortcut({
      key: ShortcutKey.PREVIOUS,
      contexts: [context],
      handler: handleNavigatePrev,
      description: `Move to previous item in ${context}`,
    });

    registerShortcut({
      key: ShortcutKey.ARROW_DOWN,
      contexts: [context],
      handler: handleNavigateNext,
      description: `Move to next item in ${context}`,
    });

    registerShortcut({
      key: ShortcutKey.ARROW_UP,
      contexts: [context],
      handler: handleNavigatePrev,
      description: `Move to previous item in ${context}`,
    });

    const openEmail = () => {
      if (isInputElementFocused()) {
        return;
      }

      if (options?.shouldBlockOpen?.()) {
        return;
      }

      if (
        selectedEmailIndex >= 0 &&
        selectedEmailIndex < filteredCommunications.length
      ) {
        const selectedEmail = filteredCommunications[selectedEmailIndex];
        if (selectedEmail) {
          const currentTab = searchParams.get('tab');
          navigate(buildEmailPreviewUrl(selectedEmail.messageId, currentTab));
        }
      }
    };

    registerShortcut({
      key: ShortcutKey.OPEN,
      contexts: [context],
      handler: openEmail,
      description: `Open selected item in ${context}`,
    });

    registerShortcut({
      key: ShortcutKey.ENTER,
      contexts: [context],
      handler: openEmail,
      description: `Open selected item in ${context}`,
    });

    const ctx = [context];
    return () => {
      unregisterShortcut(ShortcutKey.NEXT, ctx);
      unregisterShortcut(ShortcutKey.PREVIOUS, ctx);
      unregisterShortcut(ShortcutKey.ARROW_DOWN, ctx);
      unregisterShortcut(ShortcutKey.ARROW_UP, ctx);
      unregisterShortcut(ShortcutKey.OPEN, ctx);
      unregisterShortcut(ShortcutKey.ENTER, ctx);
    };
  }, [
    registerShortcut,
    unregisterShortcut,
    selectedEmailIndex,
    setSelectedEmailIndex,
    filteredCommunications,
    navigate,
    searchParams,
    context,
    options,
  ]);

  useEffect(() => {
    if (filteredCommunications.length === 0) {
      setSelectedEmailIndex(-1);
    } else if (selectedEmailIndex === -1 && filteredCommunications.length > 0) {
      setSelectedEmailIndex(0);
    } else if (selectedEmailIndex >= filteredCommunications.length) {
      setSelectedEmailIndex(filteredCommunications.length - 1);
    }
  }, [
    filteredCommunications.length,
    selectedEmailIndex,
    setSelectedEmailIndex,
  ]);

  return {
    selectedEmailIndex,
    setSelectedEmailIndex,
  };
}
